import {right} from "@popperjs/core";
import {AxiosError} from "axios";
import {
    Bin as DeleteIcon,
    DotsGrid3x3 as GroupIcon,
    DotsGrid3x3 as AddGroupIcon,
    Plus as AddPatternIcon,
    ShieldEye as DLPIcon
} from "iconoir-react";
import {useEffect, useState} from 'react'
import {Dropdown} from "react-bootstrap";
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import Tooltip from "react-bootstrap/Tooltip";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {DLPApi, type DLPPolicyPattern} from "../api/admin/generated";
import {ActionsDropdown} from "../common/ActionsDropdown";
import {ClearSelection} from "../common/ClearSelection";
import {ConfirmDialog} from "../common/ConfirmDialog";
import {DialogCloseCause} from "../common/Enums";
import {InvertSelection} from "../common/InvertSelection";
import {Pager} from "../common/Pager";
import {PageTitle} from "../common/PageTitle";
import {ReloadButton} from "../common/ReloadButton";
import {RenderAlert} from "../common/RenderAlert";
import {RowsPerPage} from "../common/RowsPerPage";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";
import {DLPColumn} from "./DLPColumn";
import {DLPGroupDialog} from "./DLPGroupDialog";
import {DLPPatternDialog} from "./DLPPatternDialog";
import {DLPTableHeader} from "./DLPTableHeader";

/*
 * REST api for accessing the back-end
 */
const dlpApi = new DLPApi(ApiConfig.config());

export const DLPPatterns = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * All patterns
     */
    const [patterns, setPatterns] = useState<DLPPolicyPattern[]>([]);
    /**
     * The total number of patterns
     */
    const [patternsCount, setPatternsCount] = useState<number>(0);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of domains to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected patterns
     */
    const [selectedPatterns, setSelectedPatterns] = useState<DLPPolicyPattern[]>([]);
    /**
     * The clicked pattern
     */
    const [clickedPattern, setClickedPattern] = useState<DLPPolicyPattern>();
    /**
     * If true, the items will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the "add pattern" modal dialog will be shown
     */
    const [showAddPattern, setShowAddPattern] = useState<boolean>(false);
    /**
     * If true, the "edit pattern" modal dialog will be shown
     */
    const [showEditPattern, setShowEditPattern] = useState<boolean>(false);
    /**
     * If true, the "add group" modal dialog will be shown
     */
    const [showAddGroup, setShowAddGroup] = useState<boolean>(false);
    /**
     * If true, the "edit pattern" modal dialog will be shown
     */
    const [showEditGroup, setShowEditGroup] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() =>
    {
        logger.debug("Load pattern count")

        // reset state when we load new data
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        dlpApi.dLPControllerGetPolicyPatternCount()
            .then(response => {
                setPatternsCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting pattern count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() =>
    {
        logger.debug("Load patterns")

        // reset the state when new data is loaded
        setSelectedPatterns([]);
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        dlpApi.dLPControllerGetPolicyPatterns((currentPage - 1) * rowsPerPage, rowsPerPage)
            .then(response => {
                setPatterns(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting patterns", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        t
    ]);

    const onChangeSelect = (checked : boolean, pattern : DLPPolicyPattern) =>
    {
        setSelectedPatterns(checked ?
            [...selectedPatterns, pattern] :
            selectedPatterns.filter(p => p.name !== pattern.name)
        );
    }

    const invertSelection = () => {
        setSelectedPatterns([...patterns].filter(p => !selectedPatterns.includes(p) && !p.inUse));
    }

    const deleteSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        dlpApi.dLPControllerDeletePolicyPatterns(selectedPatterns.map(p => p.name))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting patterns", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirm(false);
                setSelectedPatterns([]);
            })
    }

    /**
     * Called when the AddPatternDialog is closed
     */
    const onAddPatternDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowAddPattern(false);

        if (closeCause === DialogCloseCause.Succes) {
            setForceReload(true);
        }
    }

    /**
     * Called when the EditPatternDialog is closed
     */
    const onEditPatternDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowEditPattern(false);

        if (closeCause === DialogCloseCause.Succes) {
            setForceReload(true);
        }
    }

    /**
     * Called when the AddGroupDialog is closed
     */
    const onAddGroupDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowAddGroup(false);

        if (closeCause === DialogCloseCause.Succes) {
            setForceReload(true);
        }
    }

    /**
     * Called when the EditGroupDialog is closed
     */
    const onEditGroupDialogClose = () => {
        setShowEditGroup(false);
    }

    const editPattern = (pattern : DLPPolicyPattern) =>
    {
        setClickedPattern(pattern);
        setShowEditPattern(true);
    }

    const editGroup = (pattern : DLPPolicyPattern) =>
    {
        setClickedPattern(pattern);
        setShowEditGroup(true);
    }

    const onClickColumn = (pattern: DLPPolicyPattern) => {
        pattern.group ? editGroup(pattern) : editPattern(pattern);
    }

    return (
        <>
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("dlp.are-you-sure-delete-patterns")}
                           confirmButtonTitle="Delete"
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showAddPattern && <DLPPatternDialog onClose={onAddPatternDialogClose} />}
            {showEditPattern && <DLPPatternDialog editName={clickedPattern?.name} onClose={onEditPatternDialogClose} />}
            {showAddGroup && <DLPGroupDialog onClose={onAddGroupDialogClose} />}
            {showEditGroup && <DLPGroupDialog editName={clickedPattern?.name} onClose={onEditGroupDialogClose} />}

            <Row>
                <div className="d-flex flex-row align-items-center border-bottom pb-2 mb-4">
                    <div className="d-flex justify-content-start">
                        <PageTitle title={t("dlp.title")} icon={<DLPIcon />}/>
                    </div>
                    <div className="d-flex flex-fill justify-content-end">
                        <ReloadButton onClickReload={() => setForceReload(true)} />
                    </div>
                </div>
            </Row>

            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row>
                <div className="d-flex flex-row align-items-center mb-4">
                    <div className="d-flex flex-fill justify-content-end align-items-center">
                        <ActionsDropdown>
                            <Dropdown.Item onClick={() => setShowAddPattern(true)}>
                                <AddPatternIcon className="text-success"/> {t("dlp.add-pattern")}
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => setShowAddGroup(true)}>
                                <AddGroupIcon className="text-success"/> {t("dlp.add-group")}
                            </Dropdown.Item>
                        </ActionsDropdown>
                    </div>
                </div>
            </Row>

            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table className="sticky sticky-x" bordered hover>
                        {selectedPatterns.length === 0 ?
                            <DLPTableHeader>
                                <InvertSelection invertSelectionEvent={invertSelection}/>
                            </DLPTableHeader>
                            :
                            <thead className="sticky-top">
                            <tr>
                                <th colSpan={9}>
                                    <Navbar className="bg-body-tertiary ps-2">
                                        {/* Add icon to first column which will clear the selection when clicked */}
                                        <ClearSelection clearSelectionEvent={() => setSelectedPatterns([])} checked={true}/>

                                        <Navbar.Text className="ms-2">{selectedPatterns.length} selected</Navbar.Text>

                                        <div className="vr ms-4 me-2"></div>

                                        <Nav>
                                            <Nav.Link onClick={() => setShowDeleteConfirm(true)}>
                                                <DeleteIcon className="text-danger"/> {t("dlp.delete-selected")}
                                            </Nav.Link>
                                        </Nav>
                                    </Navbar>
                                </th>
                            </tr>
                            </thead>
                        }
                        <tbody>
                            {patterns?.map((p: DLPPolicyPattern) => (
                                <tr key={p.name}>
                                    <DLPColumn className="td-w-min">
                                        <Form.Check
                                            type="checkbox"
                                            className="me-2"
                                            checked={selectedPatterns.find((spattern) =>  spattern.name === p.name) !== undefined}
                                            onChange={(e) => onChangeSelect(e.target.checked, p)}
                                            disabled={p.inUse}
                                            inline
                                        />
                                        {p.group &&
                                            <OverlayTrigger
                                                placement={right}
                                                overlay={
                                                    <Tooltip>
                                                        {t("dlp.group")}
                                                    </Tooltip>
                                                }
                                            >
                                                <GroupIcon className="text-success"/>
                                            </OverlayTrigger>
                                        }
                                    </DLPColumn>

                                    <DLPColumn value={p.name} onClick={() => onClickColumn(p)}/>
                                    <DLPColumn value={p.description}  onClick={() => onClickColumn(p)}/>
                                    <DLPColumn value={p.regEx} onClick={() => onClickColumn(p)}/>
                                    <DLPColumn value={p.validator} onClick={() => onClickColumn(p)}/>
                                    <DLPColumn value={p.group ? "" : `${p.threshold}`} onClick={() => onClickColumn(p)}/>
                                    <DLPColumn value={p.action} onClick={() => onClickColumn(p)}/>
                                    <DLPColumn value={p.group ? "" : `${p.delayEvaluation}`} onClick={() => onClickColumn(p)}/>
                                    <DLPColumn value={p.matchFilter} onClick={() => onClickColumn(p)}/>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            <Row className="mt-4 align-items-baseline">
                <div className="col-4 d-flex">
                    <div className="d-flex justify-content-start align-items-center d-none d-md-inline-flex me-4">
                        {patternsCount} {t("common.results")}
                    </div>

                    <div className="d-flex justify-content-start align-items-center">
                        <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
                                     setCurrentPage={setCurrentPage}/>
                    </div>
                </div>
                <div className="col-4">
                    <div className="d-flex justify-content-center">
                        <Pager itemCount={patternsCount}
                               currentPage={currentPage}
                               setCurrentPage={setCurrentPage}
                               maxItemsPerPage={rowsPerPage}
                        />
                    </div>
                </div>
            </Row>
        </>
    );
}
